import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Proposals from './pages/Proposals';
import NewProposal from './pages/NewProposal';
import ProposalDetail from './pages/ProposalDetail';
import Agreements from './pages/Agreements';
import AgreementDetailV3 from './pages/AgreementDetailV3';
import DeliverySubpage from './pages/DeliverySubpage';
import Templates from './pages/Templates';
import CostInputTypes from './pages/CostInputTypes';
import ApiKeys from './pages/ApiKeys';
import AdminCenter from './pages/AdminCenter';
import PublicProposal from './pages/PublicProposal';
import Reports from './pages/Reports';
import Connections from './pages/Connections';
import ConnectionDetail from './pages/ConnectionDetail';
import CompletionAttestation from './pages/CompletionAttestation';
import DeliverableEvidence from './pages/DeliverableEvidence';
import AcceptInvitation from './pages/AcceptInvitation';
import ConnectorGrantSetup from './pages/ConnectorGrantSetup';
import ToastContainer from './components/Toast';
import LegacyRedirect from './components/LegacyRedirect';
import PlatformOperatorRoute from './components/PlatformOperatorRoute';
import OperatorImpersonationBanner from './components/OperatorImpersonationBanner';
import NotificationSettings from './pages/NotificationSettings';
import Unsubscribe from './pages/Unsubscribe';
import OperatorWorkspaces from './pages/internal/OperatorWorkspaces';
import OperatorWorkspaceDetail from './pages/internal/OperatorWorkspaceDetail';
import OperatorDashboard from './pages/internal/OperatorDashboard';
import OperatorAudit from './pages/internal/OperatorAudit';

// PRD-006: deprecation toast copy fired on /companies + /customers redirects.
const LEGACY_REDIRECT_HEADLINE = 'Companies and Customers are now part of Connections.';
const LEGACY_REDIRECT_BODY =
  'Verdict has simplified its data model. Everything you used to manage in Companies and Customers is now handled when you invite a counterparty workspace.';

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public Route wrapper (redirects to dashboard if already logged in)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

      {/* Public proposal view (no auth required) */}
      <Route path="/p/:shareToken" element={<PublicProposal />} />

      {/* Connection invitation landing — works whether signed in or not */}
      <Route path="/invite/:token" element={<AcceptInvitation />} />

      {/* PRD-007 — public unsubscribe confirmation. No auth. */}
      <Route path="/unsubscribe" element={<Unsubscribe />} />

      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/connections" element={<ProtectedRoute><Connections /></ProtectedRoute>} />
      <Route path="/connections/:id" element={<ProtectedRoute><ConnectionDetail /></ProtectedRoute>} />
      <Route path="/proposals" element={<ProtectedRoute><Proposals /></ProtectedRoute>} />
      <Route path="/proposals/new" element={<ProtectedRoute><NewProposal /></ProtectedRoute>} />
      <Route path="/proposals/:id" element={<ProtectedRoute><ProposalDetail /></ProtectedRoute>} />
      <Route path="/agreements" element={<ProtectedRoute><Agreements /></ProtectedRoute>} />
      <Route path="/agreements/:id" element={<ProtectedRoute><AgreementDetailV3 /></ProtectedRoute>} />
      <Route path="/agreements/:id/delivery" element={<ProtectedRoute><DeliverySubpage /></ProtectedRoute>} />
      <Route path="/agreements/:id/attestation" element={<ProtectedRoute><CompletionAttestation /></ProtectedRoute>} />
      <Route path="/agreements/:id/deliverables/:deliverableId/evidence" element={<ProtectedRoute><DeliverableEvidence /></ProtectedRoute>} />
      <Route path="/agreements/:id/connectors/slack/setup" element={<ProtectedRoute><ConnectorGrantSetup /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route
        path="/companies"
        element={
          <ProtectedRoute>
            <LegacyRedirect
              to="/connections"
              sessionKey="companies"
              headline={LEGACY_REDIRECT_HEADLINE}
              body={LEGACY_REDIRECT_BODY}
            />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <LegacyRedirect
              to="/connections"
              sessionKey="customers"
              headline={LEGACY_REDIRECT_HEADLINE}
              body={LEGACY_REDIRECT_BODY}
            />
          </ProtectedRoute>
        }
      />
      <Route path="/cost-input-types" element={<ProtectedRoute><CostInputTypes /></ProtectedRoute>} />
      {/* PRD-027 WU-E — canonical Units of Measure home (Configure → Units of
          Measure). Renders the Cost Types page deep-linked to its UoM tab. */}
      <Route path="/units-of-measure" element={<ProtectedRoute><CostInputTypes /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
      <Route path="/outcome-types" element={<Navigate to="/templates" replace />} />
      <Route path="/api-keys" element={<ProtectedRoute><ApiKeys /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminCenter /></ProtectedRoute>} />
      <Route
        path="/account/notifications"
        element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>}
      />

      {/* PRD-008 — Platform Operator Console (/internal/*). */}
      <Route path="/internal" element={<Navigate to="/internal/dashboard" replace />} />
      <Route
        path="/internal/dashboard"
        element={<PlatformOperatorRoute><OperatorDashboard /></PlatformOperatorRoute>}
      />
      <Route
        path="/internal/workspaces"
        element={<PlatformOperatorRoute><OperatorWorkspaces /></PlatformOperatorRoute>}
      />
      <Route
        path="/internal/workspaces/:id"
        element={<PlatformOperatorRoute><OperatorWorkspaceDetail /></PlatformOperatorRoute>}
      />
      <Route
        path="/internal/audit"
        element={<PlatformOperatorRoute><OperatorAudit /></PlatformOperatorRoute>}
      />

      {/* Redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OperatorImpersonationBanner />
        <AppRoutes />
        <ToastContainer />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
